// Performance utilities for QuoteRequestDetail

import { useMemo } from 'react';

/**
 * Memoized progress calculation
 *
 * Progress is measured against the original (non alternative) parts only, matching
 * the `progress` field computed in Odoo. A completed alternative counts towards its
 * original part, so quoting one alternative already brings that part to 100%, and
 * quoting the original plus its alternatives pushes the total above 100%
 * (e.g. 2 originals + 1 alternative all quoted -> 150%).
 *
 * @param {Array} lines - Array of quote lines (originals and alternatives)
 * @returns {Object} Progress statistics
 */
export const useProgressCalculation = (lines) => {
  return useMemo(() => {
    if (!lines || lines.length === 0) {
      return {
        total: 0,
        baseTotal: 0,
        completed: 0,
        inProgress: 0,
        draft: 0,
        percentage: 0,
        completedPercentage: 0,
        inProgressPercentage: 0
      };
    }

    const total = lines.length;
    // Fall back to every line when the quote only holds alternatives
    const baseTotal = lines.filter(l => !l.is_alt).length || total;
    const completed = lines.filter(l => l.state === 'completed').length;
    const inProgress = lines.filter(l => l.state === 'in_progress').length;
    const draft = lines.filter(l => l.state === 'draft').length;

    return {
      total,
      baseTotal,
      completed,
      inProgress,
      draft,
      percentage: baseTotal > 0 ? Math.round((completed / baseTotal) * 100) : 0,
      completedPercentage: baseTotal > 0 ? Math.round((completed / baseTotal) * 100) : 0,
      inProgressPercentage: baseTotal > 0 ? Math.round((inProgress / baseTotal) * 100) : 0
    };
  }, [lines]);
};
